import { useMemo } from 'react'
import { useIntl } from 'react-intl'
import Group from 'components/utils/Group'
import useCheapestOfTourV2Offer from 'hooks/TourV2/useCheapestOfTourV2Offer'
import useTourV2PurchasableOptionDetails from 'hooks/TourV2/useTourV2PurchasableOptionDetails'
import useTourV2VariationDetails from 'hooks/TourV2/useTourV2VariationDetails'
import PriceRowPriceCaption from 'LuxKit/PricePoints/PriceRowPriceCaption'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import PriceRowAgentHubCommission from 'agentHub/components/PriceRowAgentHubCommission'
import PriceRowValueDiscountWithCaption from 'LuxKit/PricePoints/Value/PriceRowValueDiscountWithCaption'
import PriceRowCaption from 'LuxKit/PricePoints/PriceRowCaption'
import LuxPlusPriceStack from 'luxPlus/components/LuxPlusPriceStack'
import type { TourV2PurchasableOptionDetails } from 'lib/tours/extractTourV2PurchasableOptionDetails'
import { useAppSelector } from 'hooks/reduxHooks'
import { getCanViewLuxPlusBenefits } from 'luxPlus/selectors/featureToggle'
import { ViewOfferButton } from './ViewOfferButton'
import LoyaltyBurnPriceDisplay from 'loyalty/components/LoyaltyBurnPriceDisplay'
import getLoyaltyProductType from 'loyalty/lib/getLoyaltyProductType'
import useSearchOfferBurnPricing from 'hooks/useSearchOfferBurnPricing'
import { getIsLuxPlusToursEnabled } from 'checkout/selectors/tourV2Selectors'

const emptyTourV2PurchasableOptionDetails = {} as { [key in keyof TourV2PurchasableOptionDetails]: undefined }

interface Props {
  offer: App.Tours.TourV2Offer | App.Tours.TourV2OfferSummary
  className?: string
  variant?: 'default' | 'condensed'
  filters?: App.OfferListFilters
  showViewButton?: boolean
}

function TourV2PriceDetails(props: Props) {
  const intl = useIntl()
  const { offer, variant = 'default', filters, showViewButton = true } = props

  const {
    cheapestPurchasableOption,
    cheapestVariation,
  } = useCheapestOfTourV2Offer(offer)

  const {
    durationInDaysCount,
  } = useTourV2VariationDetails(cheapestVariation)

  const {
    roomTypeName,
    pricing,
    memberPricing,
    valuedAt,
  } = useTourV2PurchasableOptionDetails(cheapestPurchasableOption) || emptyTourV2PurchasableOptionDetails
  const isLuxPlusMember = useAppSelector(getCanViewLuxPlusBenefits)
  const isLuxPlusToursEnabled = useAppSelector(getIsLuxPlusToursEnabled)
  const loyaltyProductType = useMemo(() => getLoyaltyProductType(offer), [offer])
  const { priceInPoints, memberPriceInPoints, pointsToBurn, pointsOnly, hideZeroCash, shouldShowBurn } = useSearchOfferBurnPricing({
    price: pricing?.price ?? 0,
    memberPrice: memberPricing?.price,
    loyaltyProductType,
    viewPriceInPoints: filters?.viewPriceInPoints,
    disabled: !pricing?.price,
    isLuxPlusEnabled: isLuxPlusToursEnabled,
  })

  const tourV2StartLocation = useMemo(
    () => {
      const variations = Object.values(offer.variations)
      if (variations.length === 0) return ['']

      const variation = variations[0]

      return [variation.startLocation || '']
    },
    [offer],
  )

  return (
    <Group direction="horizontal" horizontalAlign="space-between" verticalAlign="end">
      <VerticalSpacer gap={8}>
        <div>
          {pricing &&
            <>
              <PriceRowPriceCaption>{intl.formatMessage({
                id: 'ElSwUvn-4v9R-3Ypa4TS',
                defaultMessage: '{duration} from',
              }, { duration: durationInDaysCount })}
              </PriceRowPriceCaption>
              {shouldShowBurn && <LoyaltyBurnPriceDisplay
                totalPrice={pricing.price}
                pointsToBurn={pointsToBurn}
                priceInPoints={priceInPoints}
                memberPrice={memberPricing?.price}
                memberPriceInPoints={memberPriceInPoints}
                size="L"
                saleUnit="person"
                pointsOnly={pointsOnly}
                hideZeroCash={hideZeroCash}
                isTour
              />}
              {!shouldShowBurn && <LuxPlusPriceStack pricing={pricing} memberPricing={memberPricing} size="L" isTour />}
            </>}
          {pricing?.value && <PriceRowValueDiscountWithCaption
            data-testid="value-box"
            size="L"
            originalValue={Math.ceil(pricing.value)}
            discountPercentage={isLuxPlusMember && valuedAt ? valuedAt.memberSavingsPercentage : pricing.discountPercent}
          />}
          <PriceRowCaption>{roomTypeName}</PriceRowCaption>
        </div>
        <PriceRowAgentHubCommission
          productType={offer.productType}
          offerId={offer.id}
          offerLocations={tourV2StartLocation}
        />
      </VerticalSpacer>
      {showViewButton && <ViewOfferButton variant={variant} />}
    </Group>
  )
}

export default TourV2PriceDetails
